// A person's icon: their uploaded profile pic (R2) if they have one, else their
// emoji in a circle. Optionally tappable — used to open the real-name reveal
// (PRD §3.7). One component so avatars look identical everywhere a person shows.

import { useState } from "react";

interface AvatarLike {
  emoji: string;
  avatarUrl: string | null;
  handle?: string;
}

export function Avatar({
  person,
  size = 40,
  onClick,
  bordered = true,
  className = "",
}: {
  person: AvatarLike;
  size?: number;
  onClick?: () => void;
  /** The ringed circle. Turn off when the avatar already sits inside a bordered chip. */
  bordered?: boolean;
  className?: string;
}) {
  const [imgError, setImgError] = useState(false);
  const showImg = person.avatarUrl && !imgError;

  const inner = showImg ? (
    <img
      src={person.avatarUrl!}
      alt={person.handle ? `${person.handle}'s photo` : ""}
      onError={() => setImgError(true)}
      className="h-full w-full object-cover"
      draggable={false}
    />
  ) : (
    <span aria-hidden className="leading-none" style={{ fontSize: Math.round(size * 0.55) }}>
      {person.emoji}
    </span>
  );

  const box =
    "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-paper-deep " +
    (bordered ? "border-2 border-ink/20 " : "") +
    className;
  const style = { width: size, height: size };

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={style}
        className={box + " transition-transform hover:scale-105 active:scale-95"}
        aria-label={person.handle ? `Who is ${person.handle}?` : "Show real name"}
      >
        {inner}
      </button>
    );
  }
  return (
    <span style={style} className={box}>
      {inner}
    </span>
  );
}
